import { UseFormReturn } from "react-hook-form";
import { ListingFormValues, FormStep } from "../filters/FilterSchema"; // Import FormStep
import { useState, useEffect } from "react"; // Import useEffect
import { useListingWizard } from "@/hooks/useListingWizard";
import { useListingWizardContext } from "@/context/ListingWizardContext";
import { toast } from "sonner";
import ImageUploadArea from "./media/ImageUploadArea";
import UploadedImagesGrid from "./media/UploadedImagesGrid";
import MediaLinksFields from "./media/MediaLinksFields";
import { AxiosProgressEvent } from "axios"; // Import AxiosProgressEvent

interface MediaStepProps {
  form: UseFormReturn<ListingFormValues>;
}

const MediaStep = ({ form }: MediaStepProps) => {
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>(
    {}
  );

  const { listingId, draftListing, setCurrentStep, setStepCompleted } =
    useListingWizardContext();

  useEffect(() => {
    console.log("[MediaStep] draftListing.images:", draftListing?.images);
  }, [draftListing?.images]);
  const { uploadImageMutation, deleteImageMutation, setPrimaryImageMutation } =
    useListingWizard();

  // Helper to save image URLs to local storage (keeping for user's request, though display relies on draftListing)
  const saveImageUrlsToLocalStorage = (urls: string[]) => {
    localStorage.setItem("listingImageUrls", JSON.stringify(urls));
  };

  // Helper to load image URLs from local storage (keeping for user's request, but not directly used for display)
  const loadImageUrlsFromLocalStorage = () => {
    const storedUrls = localStorage.getItem("listingImageUrls");
    return storedUrls ? JSON.parse(storedUrls) : [];
  };

  // Effect to load image URLs from local storage on component mount (not directly used for display, but kept)
  useEffect(() => {
    const storedUrls = loadImageUrlsFromLocalStorage();
    if (storedUrls.length > 0) {
      // This part is not directly used for display as draftListing is the source of truth.
      // It's kept for the user's explicit request regarding local storage.
    }
  }, []);

  // Handle image upload
  const handleUpload = async () => {
    if (!listingId || !files.length) {
      toast.error("No files selected or listing ID not found");
      return;
    }

    // Frontend validation for image count before upload
    const currentImageCount = draftListing?.images?.length || 0;
    const totalImagesAfterUploadAttempt = currentImageCount + files.length;
    if (
      totalImagesAfterUploadAttempt < 3 ||
      totalImagesAfterUploadAttempt > 6
    ) {
      toast.error("Please upload between 3 and 6 images.");
      return;
    }

    setUploading(true);
    setUploadProgress({}); // Reset progress for new upload batch

    // Determine if any existing image is primary
    const hasPrimaryImage = draftListing?.images?.some((img) => img.is_primary);

    const uploadPromises = files.map(async (file, index) => {
      // Determine if this image should be primary:
      // It's primary if it's the first image in the current batch AND
      // there's no primary image already set for the listing.
      const isPrimary = index === 0 && !hasPrimaryImage;

      try {
        // Update progress initially
        setUploadProgress((prev) => ({
          ...prev,
          [file.name]: 0, // Start at 0%
        }));

        // Upload file with progress tracking
        console.log(
          `[MediaStep] Attempting to upload image: ${file.name} for listing ID: ${listingId}`
        );
        const uploadResult = await uploadImageMutation.mutateAsync({
          listingId,
          imageFile: file,
          isPrimary,
          onUploadProgress: (progressEvent: AxiosProgressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress((prev) => ({
              ...prev,
              [file.name]: percentCompleted,
            }));
          },
        });

        console.log(
          `[MediaStep] Successfully uploaded ${file.name}. Response:`,
          uploadResult
        );
        // Mark as complete (should be 100% from progress, but ensure)
        setUploadProgress((prev) => ({
          ...prev,
          [file.name]: 100,
        }));
        return { file, success: true, data: uploadResult }; // Return data for potential use
      } catch (error) {
        console.error(`[MediaStep] Error uploading image ${file.name}:`, error);
        toast.error(`Failed to upload ${file.name}`);
        setUploadProgress((prev) => ({
          ...prev,
          [file.name]: -1, // Indicate error
        }));
        return { file, success: false, error };
      }
    });

    const results = await Promise.all(uploadPromises);

    const allSuccessful = results.every((result) => result.success);

    if (allSuccessful) {
      setFiles([]); // Clear files only if all uploads were attempted
      toast.success("All selected images uploaded successfully!");
      // The draftListing is updated via React Query's setQueryData in useListingWizard.ts
      // The form will be reset by FormProvider's useEffect when draftListing updates.
      // Local storage is updated by FormProvider's saveCurrentStep.
    } else {
      // Keep files that failed for potential retry, or clear based on desired UX
      setFiles([]); // Clear all attempted files for simplicity, user can re-select
      toast.warning("Some images failed to upload. Please try again.");
    }

    setUploading(false);
  };

  // Handle deleting an uploaded image
  const handleDeleteImage = async (imageId: number) => {
    if (!listingId) return;

    try {
      await deleteImageMutation.mutateAsync({
        listingId,
        imageId,
      });

      toast.success("Image deleted successfully");
      // The draftListing is updated via React Query's setQueryData in useListingWizard.ts
      // The form will be reset by FormProvider's useEffect when draftListing updates.
      // Local storage is updated by FormProvider's saveCurrentStep.
    } catch (error) {
      console.error("Error deleting image:", error);
      toast.error("Failed to delete image");
    }
  };

  // Set an image as primary
  const handleSetPrimary = async (imageId: number) => {
    if (!listingId) return;

    try {
      await setPrimaryImageMutation.mutateAsync({
        listingId,
        imageId,
      });

      toast.success("Primary image updated");
      // The draftListing is updated via React Query's setQueryData in useListingWizard.ts
      // The form will be reset by FormProvider's useEffect when draftListing updates.
      // Local storage is updated by FormProvider's saveCurrentStep.
    } catch (error) {
      console.error("Error setting primary image:", error);
      toast.error("Failed to update primary image");
    }
  };

  // Determine if the "Upload all" button should be disabled
  const totalImagesAfterSelection =
    (draftListing?.images?.length || 0) + files.length;

  const isUploadButtonDisabled =
    uploading ||
    files.length === 0 ||
    totalImagesAfterSelection < 3 ||
    totalImagesAfterSelection > 6;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium">Media</h3>
        <p className="text-sm text-gray-500 mb-4">
          Add images and videos of your property
        </p>
      </div>

      {/* Image Upload */}
      <div className="border rounded-md p-4 space-y-4">
        <h4 className="text-base font-medium">Property Images</h4>

        {/* Image upload area component */}
        <ImageUploadArea
          files={files}
          setFiles={setFiles}
          uploading={uploading}
          uploadProgress={uploadProgress}
          handleUpload={handleUpload}
          isUploadButtonDisabled={isUploadButtonDisabled} // Pass disabled state
        />

        {/* Uploaded images component */}
        {draftListing?.images && draftListing.images.length > 0 && (
          <UploadedImagesGrid
            images={draftListing.images}
            onSetPrimary={handleSetPrimary}
            onDeleteImage={handleDeleteImage}
          />
        )}
      </div>

      {/* Media links component */}
      <MediaLinksFields form={form} />
    </div>
  );
};

export default MediaStep;
